<template>
  <div class="ws-modal">
    <form class="ws-modal__form" @submit.prevent="submit">
      <header class="ws-modal__header">
        <h2 class="has-text-weight-bold is-size-1">
          <span>{{ $t(`${isNewItem ? 'Создать' : 'Редактировать'} заявку на ДЭ`) }}</span>
        </h2>
      </header>
      <div class="ws-modal__content" :class="{'is-loading': isPending}">
        <ws-role-node :only="['front_curator']">
          <div class="columns">
            <div class="column is-12">
              <p class="is-size-3">
                Укажите одну (первую) экзаменационную/учебную группу (для определения образовательной организации). После нажатия кнопки "Создать" можно будет отредактировать существующие или добавить дополнительные смены и экзаменационные/учебные группы.
              </p>
            </div>
          </div>
        </ws-role-node>

        <div class="columns is-multiline">
          <fragment v-if="!isNewItem">
            <div class="column is-10">
              <b-field
                class="ws-field is-required"
                :label="$t('Название экзамена')"
                :type="{'is-danger': errors.has('name')}"
                :message="errors.first('name')"
              >
                <b-input
                  class="ws-field__control"
                  data-id="modal-form_exam-name-input"
                  v-model="newItem.name"
                  v-validate="{ required: true, max: 255, validateSpecialCharacters: true }"
                  :data-vv-as="$t('Название экзамена')"
                  name="name"
                />
              </b-field>
            </div>
            <div class="column is-2">
              <div class="field-upd-title">
                <b-checkbox v-model="updateTitle" class="b-checkbox">
                  <b>Обновить наименование</b><br>
                </b-checkbox>
              </div>
            </div>
          </fragment>
          <div class="column is-4">
            <div class="ws-datepicker">
              <b-field class="ws-field"
                :label="$t('Подготовительный день')"
                :type="{
                  'is-danger': errors.has('day_cm1'),
                  'is-warning': (errorDayCmDayOff || validateYear) && !errors.first('day_cm1')
                }"
                :message="errors.first('day_cm1') || errorDayCmDayOff || validateYear"
              >
                <ws-datepicker
                  data-id="modal-form_datepicker-day-cm1"
                  v-model="newItem.day_cm1"
                  v-validate="{
                    required: true,
                    correctDate: true
                  }"
                  :value="newItem.day_cm1"
                  :data-vv-as="$t('Подготовительный день')"
                  @input="onChangeDate"
                  name="day_cm1"
                />
              </b-field>
            </div>
          </div>
          <div class="column is-8" data-id="modal-form_competence-column">
            <b-field class="ws-field"
              :label="$t('Профессия/специальность (ДЭ)')"
              :type="{'is-danger': errors.has('competence')}"
              :message="errors.first('competence')"
            >
              <ws-refbook-field
                entity="competence"
                field="name.translate.ru"
                name="competence"
                :guid.sync="newItem.competence.guid"
                :item.sync="newItem.competence.item"
                :query="{wo_skill: true}"
                v-model="newItem.competence.value"
                v-validate="{
                  required: true,
                  selected: newItem.competence.guid
                }"
                :data-vv-as="$t('Профессия/специальность (ДЭ)')"
                :disabled="!isNewItem"
                :isFocus="true"
                :filterFunction="isHiddenOptionCompetence"
              >
                <template slot="empty" v-text="$t('Ничего не найдено')"></template>
              </ws-refbook-field>
            </b-field>
          </div>
          <div
            class="column is-5"
            data-id="modal-form_qualification-column"
          >
            <b-field class="ws-field"
              :label="$t('Квалификация')"
              :type="{'is-danger': errors.has('complexity_level')}"
              :message="errors.first('complexity_level')"
            >
              <WsSelectComplexityCode
                :value="newItem.complexity_level"
                :disabled="!newItem.competence.guid"
                :competence="newItem.competence"
                :dateFilter="newItem.day_cm1"
                :placeholder="$t('')"
                open-on-focus
                :on-change="selectComplexy"
                v-validate="{
                  required: true,
                  validateSpecialCharacters: true,
                  max: 255
                }"
                :name="'complexity_level'"
                :class="'ws-field__control'"
                :isDependsOfCompetence="true"
              />
            </b-field>
          </div>

          <div class="column is-7" data-id="modal-form_de-center-column">
            <b-field class="ws-field"
              :label="$t('Центр проведения демонстрационного экзамена')"
              :type="{'is-danger': errors.has('de_center')}"
              :class="{'cpde-invisible': !newItem.de_center.visible}"
              :message="errors.first('de_center')"
            >
              <ws-refbook-field
                entity="de-centers"
                field="name.display"
                name="de_center"
                :guid.sync="newItem.de_center.guid"
                :item.sync="newItem.de_center.item"
                v-model="newItem.de_center.value"
                v-validate="{
                  required: true,
                  selected: newItem.de_center.guid
                }"
                :data-vv-as="$t('Центр проведения демонстрационного экзамена')"
                :isFocus="true"
                :custom-formatter="cpdeFormatter"
                >
                <template slot="item" slot-scope="props">
                  <div class="is-flex">
                    <small v-if="props.option.visible === false">
                      <ws-icon
                      name="stop"
                      color="red"
                      height="15"
                      width="15"
                      class="icon-stop"
                      />
                    </small>
                    <p>{{ props.option.name.display }}</p>
                  </div>
                  <small class="has-text-grey">{{ getDeCenterOptionSubTitle(props.option) }}</small>
                </template>
                <template slot="empty" v-text="$t('Ничего не найдено')"></template>
              </ws-refbook-field>
            </b-field>
          </div>
        </div>

        <!-- Shifts and groups -->
        <div class="columns is-multiline" v-if="isNewItem && isCreateShift">
          <ws-request-exam-group-form
            isExamForm
            :complexityLevel="newItem.complexity_level"
            :exam="newItem"
            :disabled-dates="!newItem.complexity_level || !newItem.day_cm1"
            :values="group"
          />
        </div>

        <ws-request-exam-type-form
          v-validate="{
            required: true,
            typesRequired: {
              schema: typesSchema
            }
          }"
          :items="typesSchema"
          :value="newItem.application_type"
          name="application_type"
          v-model="newItem.application_type"
        />
        <div class="columns">
          <div class="column is-12" v-if="errors.has('application_type')">
            <p class="has-text-danger is-size-5">{{ $t('Необходимо выбрать тип заявки') }}</p>
          </div>
        </div>
        <ws-request-exam-code-type-form
          v-if="isShowCodeType"
          v-model="newItem.codeType"
          :value="newItem.codeType"
          :items="this.codeTypesSchema"
          name="codeType"
        />
        <div class="columns">
          <div class="column is-size-4">
            <div>
              <b-checkbox
                name="remote"
                class="b-checkbox__centered"
                v-model="newItem.remote_experts"
              >
                <b>{{ $t('Удаленное присутствие одного или нескольких экспертов (ГЭ или Эксперт)') }}</b>
              </b-checkbox>
            </div>
            <div>
              <b-checkbox
                name="remote"
                class="b-checkbox__centered"
                v-model="newItem.remote_members"
              >
                <b>{{ $t('Участники присутствуют на ДЭ дистанционно (удаленно)') }}</b>
              </b-checkbox>
            </div>
          </div>
        </div>
      </div>
      <footer class="ws-modal__footer">
        <div class="columns">
          <div class="column is-8"></div>
          <div class="column is-4 is-vcentered">
            <div class="has-text-right-desktop">
              <button
                data-id="modal-form_action-button"
                class="ws-button button is-large is-primary is-fullwidth"
                :class="{'is-loading': isPending}"
              >
                <span class="is-size-4 has-text-weight-bold" v-if="!isNewItem" v-text="$t('Редактировать')"></span>
                <span class="is-size-4 has-text-weight-bold" v-else v-text="$t('Создать')"></span>
              </button>
            </div>
          </div>
        </div>
      </footer>
    </form>
    <aside class="ws-modal__sidebar">
      <button
        class="ws-button button is-text"
        @click="close"
      >
        <ws-icon
          class="ws-button__icon"
          name="close-thin"
          color="#000000"
          :fill="true"
          :title="$t('Закрыть')"
          width="17"
          height="18"
        />
      </button>
    </aside>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import WsDatepicker from '@/components/common/WsDatepicker'
import WsSelectComplexityCode from '@/components/common/WsSelectComplexityCode.vue'
import WsRequestExamCodeTypeForm from '../WsRequestExamCodeTypeForm.vue'
import WsRequestExamGroupForm from '../WsRequestExamGroupForm.vue'
import { examFormValuesSerializer, examSubmitSerializer } from '@/utils/examSerializers'
import { parseTitle } from '@/utils/parse-exam-title'
import { getShiftNumbers, toUTCDay, validateCurrentYear } from '@/utils/helpers'
import {
  APPLICATION_TYPES,
  EXAM_DEFAULT_VALUES,
  LEVEL_SCHEMA,
  TYPES_SCHEMA
} from '@/services/constants.service'

export default {
  name: 'WsRequestExamsForm',
  components: { WsRequestExamCodeTypeForm, WsDatepicker, WsSelectComplexityCode, WsRequestExamGroupForm },
  props: {
    item: {
      type: Object,
      default: () => ({ ...EXAM_DEFAULT_VALUES })
    }
  },
  data () {
    return {
      codeTypesSchema: LEVEL_SCHEMA,
      newItem: { ...EXAM_DEFAULT_VALUES },
      typesSchema: TYPES_SCHEMA,
      updateTitle: true,
      shiftNumbers: getShiftNumbers(),

      group: {
        groups: [{
          education_group: {
            guid: null
          },
          student: null,
          independent_estimate_participants_number: null,
          id: 1
        }],
        shift: {},
        job_issued_at: null,
        started_at: null,
        ended_at: null,
        number: 1
      }
    }
  },
  watch: {
    item: {
      immediate: true,
      handler (item) {
        this.newItem = examFormValuesSerializer(item)
      }
    },
    'newItem.day_cm1': {
      immediate: true,
      handler () {
        this.updateExamTitle()
      }
    },
    'newItem.competence': {
      immediate: true,
      deep: true,
      handler (val) {
        if (!val.guid && this.newItem.complexity_level) {
          this.newItem.complexity_level = null
        }
        this.updateExamTitle()
      }
    },
    updateTitle: {
      immediate: true,
      handler (isUpdate) {
        if (isUpdate) {
          this.updateExamTitle()
        }
      }
    }
  },
  computed: {
    ...mapGetters('calendarGeneral', ['datesCalendar']),
    ...mapState('exams', {
      isUpdatePending: 'isUpdatePending',
      isCreatePending: 'isCreatePending'
    }),
    ...mapState('authentication', ['currentRole', 'user']),
    errorDayCmDayOff () {
      // или год из значения или текущий год
      const currentYear = this.newItem.day_cm1 ? new Date(this.newItem.day_cm1).getFullYear() : new Date().getFullYear()
      // получение нужного массива по году из стора
      const events = this.datesCalendar && this.datesCalendar[currentYear]
        ? this.datesCalendar[currentYear].list : []
      // входил ли выбранная дата в массив, если входит, значит это или выходной или прадничный день
      return events.find(function (elem) {
        return this.newItem.day_cm1 && elem.date.getTime() === new Date(toUTCDay(this.newItem.day_cm1).toISOString()).getTime()
      }.bind(this)) ? 'Обратите внимание, в датах ДЭ присутствуют выходные или праздничные дни' : ''
    },
    validateYear () {
      const message = validateCurrentYear(this.newItem.day_cm1)
      return message
    },
    isPending () {
      return this.isCreatePending || this.isUpdatePending
    },
    isNewItem () {
      return !this.item.guid
    },
    isShowCodeType () {
      const { FINAL = '' } = APPLICATION_TYPES
      const { application_type: type = [] } = this.newItem
      return type.includes(FINAL)
    },
    isCreateShift () {
      const roles = ['front_curator', 'front_curator_institution', 'front_university']
      return roles.includes(this.currentRole)
    }
  },
  mounted () {
    this.$validator.pause()
  },
  methods: {
    ...mapActions('exams', ['create', 'update']),
    ...mapActions('exams/shifts', {
      createShift: 'create'
    }),
    onChangeDate () {
      this.newItem.complexity_level = null
    },
    cpdeFormatter ({ name }) {
      return name.translate.ru.trim() || name.display.trim()
    },
    updateExamTitle () {
      if (this.updateTitle) {
        const { name: competenceName = null } = this.newItem.competence.item
        const name = competenceName ? competenceName.translate.ru : ''

        this.newItem.name = parseTitle(name, this.newItem.day_cm1, this.newItem.complexity_level)
      }
    },
    selectComplexy (value) {
      this.newItem.complexity_level = value
      this.updateExamTitle()
    },
    getDeCenterOptionSubTitle (option) {
      return [
        option.address.region ? option.address.region.title.display : '',
        option.address.locality ? option.address.locality.title.display : '',
        option.address.address.display
      ].filter(Boolean).join(', ')
    },
    async submit () {
      this.$validator.resume()
      this.$validator.validateAll().then(async (result) => {
        if (result) {
          const exam = examSubmitSerializer(this.newItem, this.item)

          if (this.isNewItem) {
            const newExam = await this.create(exam)

            if (this.isCreateShift) {
              // Create new shift
              const newShift = await this.createShift([{
                'number': this.group.number,
                'job_issued_at': this.group.job_issued_at,
                'started_at': this.group.started_at,
                'ended_at': this.group.ended_at
              }, `/${newExam.guid}/shifts`])

              // Create new exam group
              await this.createShift([{
                'shift': {
                  'guid': newShift.guid
                },
                'education_group': {
                  'guid': this.group.groups[0].education_group.guid
                },
                'student': this.group.groups[0].student
              }, `/${newExam.guid}/shifts/${newShift.guid}/groups`])
            }

            this.$buefy.toast.open({
              message: this.$t(`Заявка ${this.newItem.name} успешно создана`),
              type: 'is-success'
            })
          } else {
            await this.update([this.newItem.guid, exam])
            this.$buefy.toast.open({
              message: this.$t(`Заявка ${this.newItem.name} успешно обновлена`),
              type: 'is-success'
            })
          }
          this.$emit('editExam', this.newItem)
          this.$parent.close()
        }
      })
    },
    close () {
      this.$parent.close()
    },
    isHiddenOptionCompetence (option) {
      const df = this.newItem.day_cm1
      if (df == null) {
        return true
      }
      const codeDates = option.code_dates.filter(el => new Date(el.activeFrom) <= df && df <= new Date(el.activeTo))
      return codeDates.length !== 0
    }
  }
}
</script>
<style lang="scss" scoped>
.field-upd-title {
  display: flex;
  align-items: center;
  height: 100%;
}

.cpde-invisible {
  /deep/ .ws-field__control.autocomplete .input {
    padding-left: 30px;
  }

  /deep/ .control {
    position: relative;

    &::before {
      content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='red' d='M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zM124 296c-6.6 0-12-5.4-12-12v-56c0-6.6 5.4-12 12-12h264c6.6 0 12 5.4 12 12v56c0 6.6-5.4 12-12 12H124z'/%3E%3C/svg%3E");
      width: 12px;
      height: 12px;
      position: absolute;
      left: 14px;
      z-index: 1;
      display: inline-block;
      top: 24px;
    }
  }
}

.ml-1 {
  margin-left: 1rem;
}

.icon-stop {
  margin-right: 5px;
}
</style>
